import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const ScheduleEditor = () => {
  const [users, setUsers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('');
  const [weekStart, setWeekStart] = useState('');
  const [shifts, setShifts] = useState([]);

  useEffect(() => {
    loadData();
    
    // Set current week's Monday as default
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    setWeekStart(monday.toISOString().split('T')[0]);
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, schedulesRes] = await Promise.all([
        axios.get('/admin/users'),
        axios.get('/schedules')
      ]);
      setUsers(usersRes.data.filter(u => u.role === 'driver'));
      setSchedules(schedulesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const getDaysOfWeek = () => {
    if (!weekStart) return [];
    
    const days = [];
    const start = new Date(weekStart);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push({
        date: date.toISOString().split('T')[0],
        dayName: date.toLocaleDateString('de-DE', { weekday: 'short' }),
        dayNum: date.getDate()
      });
    }
    return days;
  };

  const handleAddShift = (date) => {
    setShifts([...shifts, {
      date,
      start_time: '09:00',
      end_time: '17:00',
      location: 'Hauptfiliale'
    }]);
  };

  const handleUpdateShift = (index, field, value) => {
    const updated = [...shifts];
    updated[index][field] = value;
    setShifts(updated);
  };

  const handleRemoveShift = (index) => {
    setShifts(shifts.filter((_, i) => i !== index));
  };

  const handleSaveSchedule = async () => {
    if (!selectedUser || !weekStart || shifts.length === 0) {
      toast.error('Bitte Mitarbeiter, Woche und mindestens eine Schicht auswählen');
      return;
    }

    try {
      await axios.post('/schedules', {
        user_id: selectedUser,
        week_start: weekStart + 'T00:00:00',
        shifts: shifts
      });
      
      toast.success('Arbeitsplan erstellt');
      setShifts([]);
      setSelectedUser('');
      loadData();
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Arbeitsplan wirklich löschen?')) return;

    try {
      await axios.delete(`/schedules/${scheduleId}`);
      toast.success('Arbeitsplan gelöscht');
      loadData();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const daysOfWeek = getDaysOfWeek();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="schedule-editor">
      <div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-2">
          Arbeitsplanung
        </h1>
        <p className="text-zinc-400">Schichten planen und verwalten</p>
      </div>

      {/* Create Schedule Form */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-white">Neue Arbeitsplanung erstellen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="user">Mitarbeiter *</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="user-select">
                  <SelectValue placeholder="Mitarbeiter auswählen" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {users.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="week">Woche (Montag) *</Label>
              <Input
                id="week"
                type="date"
                value={weekStart}
                onChange={(e) => setWeekStart(e.target.value)}
                className="bg-zinc-950 border-zinc-800"
                data-testid="week-input"
              />
            </div>
          </div>

          {/* Week Days */}
          {weekStart && (
            <div>
              <Label>Schichten hinzufügen</Label>
              <div className="grid grid-cols-1 md:grid-cols-7 gap-2 mt-2">
                {daysOfWeek.map(day => (
                  <Button
                    key={day.date}
                    variant="outline"
                    className="border-zinc-700 hover:bg-zinc-800"
                    onClick={() => handleAddShift(day.date)}
                    data-testid={`add-shift-${day.dayName}`}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {day.dayName}<br />{day.dayNum}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Shifts List */}
          {shifts.length > 0 && (
            <div className="space-y-3">
              <Label>Geplante Schichten</Label>
              {shifts.map((shift, index) => {
                const dayInfo = daysOfWeek.find(d => d.date === shift.date);
                return (
                  <div key={index} className="flex gap-2 items-end p-4 bg-zinc-800 rounded-lg">
                    <div className="flex-shrink-0">
                      <div className="text-xs text-zinc-400">Tag</div>
                      <div className="font-medium text-white">
                        {dayInfo?.dayName} {dayInfo?.dayNum}
                      </div>
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Von</Label>
                      <Input
                        type="time"
                        value={shift.start_time}
                        onChange={(e) => handleUpdateShift(index, 'start_time', e.target.value)}
                        className="bg-zinc-950 border-zinc-700 h-9"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Bis</Label>
                      <Input
                        type="time"
                        value={shift.end_time}
                        onChange={(e) => handleUpdateShift(index, 'end_time', e.target.value)}
                        className="bg-zinc-950 border-zinc-700 h-9"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Standort</Label>
                      <Input
                        value={shift.location}
                        onChange={(e) => handleUpdateShift(index, 'location', e.target.value)}
                        className="bg-zinc-950 border-zinc-700 h-9"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 hover:bg-red-950"
                      onClick={() => handleRemoveShift(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          <Button 
            onClick={handleSaveSchedule} 
            className="w-full bg-primary hover:bg-primary/90"
            disabled={!selectedUser || shifts.length === 0}
            data-testid="save-schedule-btn"
          >
            <Save className="mr-2 h-4 w-4" />
            Arbeitsplan speichern
          </Button>
        </CardContent>
      </Card>

      {/* Existing Schedules */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-white">Vorhandene Arbeitspläne</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {schedules.length > 0 ? (
              schedules.map(schedule => {
                const user = users.find(u => u.user_id === schedule.user_id);
                return (
                  <div 
                    key={schedule.schedule_id}
                    className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg border border-zinc-700"
                  >
                    <div>
                      <div className="font-medium text-white">{user?.name || 'Unbekannt'}</div>
                      <div className="text-sm text-zinc-400">
                        Woche vom {new Date(schedule.week_start).toLocaleDateString('de-DE')} - {schedule.shifts.length} Schichten
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 hover:bg-red-950"
                      onClick={() => handleDeleteSchedule(schedule.schedule_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-zinc-500">
                Noch keine Arbeitspläne erstellt
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScheduleEditor;
